import React, { useMemo, useCallback, useState } from 'react'
import { DataManagementGrid } from '../Common/DataManagementGrid'
import { checklistColumns, checklistCustomHeader } from '../../Data/StudentManagement/ChecklistListData'
import { useDataManagement } from '../../Hooks/useDataManagement'
import { getStudentIdData } from '../../lib/studentchecklistview'
import { deleteIdData, updateData } from '../../lib/studentchecklist'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import ChecklistModal from './ChecklistModal'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { ConfirmModal } from '../Common/ConfirmModal'

const GlobalCheckList = ({ slug, isStudent }) => {
	const layout = useLayout()
	const role = useRoleBasedUrl()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editingItem, setEditingItem] = useState(null)
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deleteId, setDeleteId] = useState(null)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	// Create a fetch function that uses the slug
	const fetchChecklistData = useCallback(async () => {
		if (!slug) return []
		const tasks = await getStudentIdData(slug)
		return [...tasks].sort((a, b) => a.id - b.id)
	}, [slug])

	const { data: tasklist, loading, searchQuery, setSearchQuery,  fetchData } = useDataManagement(fetchChecklistData, deleteIdData)

	// Handle progress change
	const handleProgressChange = useCallback(
		async (id, currentProgress) => {
			try {
				await updateData({
					id: id,
					isProgress: !currentProgress
				})
				await fetchData() // Refresh data
			} catch (error) {
				console.error('Error updating progress:', error)
			}
		},
		[fetchData]
	)

	// Handle done change
	const handleDoneChange = useCallback(
		async (id, currentDone) => {
			try {
				await updateData({
					id: id,
					isDone: !currentDone
				})
				await fetchData() // Refresh data
			} catch (error) {
				console.error('Error updating done status:', error)
			}
		},
		[fetchData]
	)

	const handleEdit = (item) => {
		setEditingItem(item)
		setIsModalOpen(true)
	}

	const handleAddTask = () => {
		setEditingItem(null)
		setIsModalOpen(true)
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
	}

	const handleDeleteItem = (id) => {
		setDeleteId(id)
		setShowConfirmModal(true)
	}

	const confirmDelete = async () => {
		if (deleteId) {
			try {
				await deleteIdData(deleteId)
				await fetchData()
				showToastMessage('Checklist item deleted successfully!')
			} catch (error) {
				console.error('Error deleting checklist item:', error)
			} finally {
				setDeleteId(null)
			}
		}
	}

	// Add slug to each item for edit links
	const enrichedTasklist = useMemo(() => {
		return tasklist.map((item) => ({
			...item,
			slug: slug
		}))
	}, [tasklist, slug])

	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseCheckList"
						aria-expanded="false"
						aria-controls="collapseCheckList"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5 className="mb-0">Checklist</h5>
				</div>
			</div>
			<div className="collapse" id="collapseCheckList">
				<div className="card-body p-0">
					<DataManagementGrid
						title=""
						subTitle=""
						breadcrumbTitle=""
						breadcrumbMainTitle=""
						columns={(layout, role) => checklistColumns(layout, role, isStudent, handleProgressChange, handleDoneChange, slug, handleEdit)}
						data={enrichedTasklist}
						loading={loading}
						searchQuery={searchQuery}
						setSearchQuery={setSearchQuery}
						searchPlaceholder="Search checklist items..."
						createButtonText=""
						createButtonLink=""
						onDelete={handleDeleteItem}
						searchFields={['pname', 'urgency', 'startMonth', 'endMonth', 'tDate']}
						noDataMessage="No checklist items found"
						customHeaderComponent={checklistCustomHeader(searchQuery, setSearchQuery, slug, layout, role, isStudent, handleAddTask)}
					/>
				</div>
			</div>
			<ChecklistModal
				isOpen={isModalOpen}
				toggle={toggleModal}
				studentId={slug}
				editingItem={editingItem}
				onSave={fetchData}
			/>
			<ConfirmModal
				isOpen={showConfirmModal}
				toggle={() => setShowConfirmModal(false)}
				onConfirm={confirmDelete}
				title="Delete Checklist Item"
				message="Are you sure you want to delete this checklist item? This action cannot be undone."
			/>
		</div>
	)
}

export { GlobalCheckList }
